import React, { useState } from "react";

import { isEmpty, keyBy, some } from "lodash/fp";

import { Stack, Typography } from "@mui/material";

import SettingAlert from "@app/analytics/account/components/SettingAlert";
import useInviteWorkspaceMembersForm, {
  WORKSPACE_INVITE_FIELD_ARRAY_NAME,
} from "@app/analytics/account/WorkspaceSettings/hooks/useInviteWorkspaceMembersForm";
import { WorkspaceInviteInputs } from "@app/analytics/account/WorkspaceSettings/WorkspaceWizard/types";
import BasicModal from "@app/analytics/components/BasicModal";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useInviteToWorkspace from "@app/analytics/hooks/workspace/useInviteToWorkspace";
import { WorkspaceUserInviteBody } from "@app/common/account/api";
import { toast } from "@app/common/hooks/useToasts";
import { isResponseErrorWithStatus, pluralizePhrase } from "@app/common/utils";
import {
  EnterpriseRole,
  EnterpriseWorkspaceTeam,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import InviteWorkspaceMembersForm from "../components/InviteWorkspaceMembersForm";

const FORM_ID = "add-member-to-workspace";
const EMPTY_TEAMS: EnterpriseWorkspaceTeam[] = [];

const DEFAULT_ERROR_MESSAGE =
  "Something went wrong when sending invites. Please try again or refresh the page.";
const ALREADY_INVITED_ERROR_MESSAGE =
  "Some users are already in another workspace. Users must leave their current workspace to be invited.";

interface AddMemberToWorkspaceProps {
  onClose: () => void;
  open: boolean;
  workspaceName: string;
}

const toInviteeBody = (
  formValues: WorkspaceInviteInputs,
  teams: EnterpriseWorkspaceTeam[]
): WorkspaceUserInviteBody => {
  const team = teams.find((team) => team.name === formValues.team);
  const teamRole = formValues.role
    ? (formValues.role as EnterpriseRole)
    : undefined;

  return {
    email: formValues.email,
    teamId: team?.id,
    teamRole,
  };
};

const AddMemberToWorkspaceModal = ({
  onClose,
  open,
  workspaceName,
}: AddMemberToWorkspaceProps) => {
  const [errorAlertMessage, setErrorAlertMessage] = useState<string | null>(
    null
  );

  const { licenses } = useBillingContext();
  const { workspace } = useWorkspace();
  const inviteUsersToWorkspace = useInviteToWorkspace();

  const { form, fieldArray } = useInviteWorkspaceMembersForm();

  const sendInvites = () => {
    const formValues = form
      .getValues(WORKSPACE_INVITE_FIELD_ARRAY_NAME)
      // filter out any inputs that have an empty email
      .filter(({ email }) => Boolean(email));

    const invites = formValues.map((user) =>
      toInviteeBody(user, workspace.teams)
    );

    inviteUsersToWorkspace.mutate(
      { invites, workspaceId: workspace.id },
      {
        onSuccess: (response) => {
          const failures = response?.failures ?? [];
          if (isEmpty(failures)) return handleClose();

          const failureByEmail = keyBy("email", failures);

          let numRemovedInputs = 0;

          // first, remove the invites that were successful. use a rolling index since
          // fieldArray.remove appears to mutate the array
          formValues.forEach((input, i) => {
            if (!failureByEmail[input.email]) {
              fieldArray.remove(i - numRemovedInputs);
              numRemovedInputs++;
            }
          });

          const updatedFormValues = form.getValues(
            WORKSPACE_INVITE_FIELD_ARRAY_NAME
          );

          // next, set the errors on the appropriate inputs w/ updated indices
          updatedFormValues.forEach((input, i) => {
            const failureForEmail = failureByEmail[input.email];
            if (failureForEmail) {
              form.setError(`${WORKSPACE_INVITE_FIELD_ARRAY_NAME}.${i}.email`, {
                type: "custom",
                message:
                  failureForEmail.errorCode === 409
                    ? "User is already in another Workspace"
                    : "Failed to invite member",
              });
            }
          });

          if (some({ errorCode: 409 }, failures)) {
            setErrorAlertMessage(ALREADY_INVITED_ERROR_MESSAGE);
          } else {
            setErrorAlertMessage(DEFAULT_ERROR_MESSAGE);
          }
        },
        onSettled: (response) => {
          // since this is a bulk endpoint, some of the invites may have
          // successfully gone through, so show this toast onSettled instead of
          // only on success
          if (!isEmpty(response?.invites)) {
            toast.success(
              `${pluralizePhrase(response?.invites?.length ?? 0, "member")} invited to ${workspace.name}`
            );
          }
        },
        onError: (error) => {
          if (isResponseErrorWithStatus(error, 402)) {
            // shouldn't ever hit this case because the form won't allow users to input more emails
            // than they have available licenses, but handle just in case.
            setErrorAlertMessage(
              "All licenses used. Please purchase additional licenses to add more members to your Workspace."
            );
          } else {
            setErrorAlertMessage(DEFAULT_ERROR_MESSAGE);
          }
        },
      }
    );
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <BasicModal
      formId={FORM_ID}
      loading={inviteUsersToWorkspace.isPending}
      onClose={handleClose}
      open={open}
      submitText="Send Invites"
      disabled={licenses.numUnclaimed === 0}
      title="Add Workspace Members"
      sx={{ minWidth: "720px", overflowY: "auto" }}
    >
      <Stack spacing={3}>
        <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
          Invite others to join your {workspaceName} Workspace
        </Typography>
        <Stack>
          <SettingAlert
            severity="error"
            show={Boolean(errorAlertMessage)}
            onHide={() => setErrorAlertMessage(null)}
            timeout={15000}
          >
            {errorAlertMessage}
          </SettingAlert>
          <InviteWorkspaceMembersForm
            context="modal"
            fieldArray={fieldArray}
            form={form}
            formId={FORM_ID}
            onSubmit={sendInvites}
            requireUsers
            teams={workspace.teams ?? EMPTY_TEAMS}
          />
        </Stack>
      </Stack>
    </BasicModal>
  );
};

export default AddMemberToWorkspaceModal;
